import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const severityFilters = ['All', 'Good', 'Warning', 'Failing'];

const severityStyles = {
  Good: 'border-emerald-400/60 bg-emerald-400/15 text-emerald-200',
  Warning: 'border-amber-400/60 bg-amber-400/15 text-amber-100',
  Failing: 'border-rose-500/70 bg-rose-500/15 text-rose-100',
};

function CurrentAlerts() {
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';

  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('All');
  const [toast, setToast] = useState(null);
  const [focusAlert, setFocusAlert] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/get_alerts.php?username=${encodeURIComponent(user)}`);
      const data = await res.json();
      setAlerts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await fetch(`/get_user_devices.php?username=${encodeURIComponent(user)}`);
        const data = await res.json();
        setDevices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setDevices([]);
      }
    };

    fetchDevices();
  }, [user]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const availableUnits = useMemo(() => {
    const units = new Set();
    devices.forEach((device) => {
      if (device.unit) units.add(device.unit);
    });
    return Array.from(units);
  }, [devices]);

  const filteredAlerts = useMemo(() => {
    const matcher = search.toLowerCase();
    return alerts.filter((alert) => {
      const matchesSearch =
        !matcher ||
        (alert.alert_name || '').toLowerCase().includes(matcher) ||
        (alert.select_unit || '').toLowerCase().includes(matcher) ||
        (alert.description || '').toLowerCase().includes(matcher);
      const matchesSeverity = severityFilter === 'All' || alert.severity === severityFilter;
      return matchesSearch && matchesSeverity;
    });
  }, [alerts, search, severityFilter]);

  const openEditModal = (alert) => {
    setEditModal({
      ...alert,
      alert_active: Number(alert.alert_active) === 1,
      assigned_users: alert.assigned_users
        ? alert.assigned_users
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
      threshold_x: alert.threshold_x ?? '',
      threshold_y: alert.threshold_y ?? '',
      threshold_z: alert.threshold_z ?? '',
    });
    setEmailDraft('');
  };

  const updateEditField = (field, value) => {
    setEditModal((prev) => ({ ...prev, [field]: value }));
  };

  const addEditRecipient = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const trimmed = emailDraft.trim();
      if (!trimmed) return;
      if (editModal.assigned_users.includes(trimmed)) {
        setToast({ message: 'Recipient already exists.', success: false });
        return;
      }
      setEditModal((prev) => ({
        ...prev,
        assigned_users: [...prev.assigned_users, trimmed],
      }));
      setEmailDraft('');
    }
  };

  const removeEditRecipient = (index) => {
    setEditModal((prev) => ({
      ...prev,
      assigned_users: prev.assigned_users.filter((_, idx) => idx !== index),
    }));
  };

  const toggleActive = async (alert) => {
    if (togglingId) return;
    const newValue = Number(alert.alert_active) === 1 ? 0 : 1;
    setTogglingId(alert.id);
    try {
      const res = await fetch('/update_alert.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: Number(alert.id),
          username: user,
          alert_active: newValue,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update alert');
      }
      setAlerts((prev) =>
        prev.map((item) => (item.id === alert.id ? { ...item, alert_active: newValue } : item))
      );
      setToast({
        message: `Alert ${newValue === 1 ? 'activated' : 'paused'} successfully.`,
        success: true,
      });
    } catch (error) {
      console.error(error);
      setToast({ message: 'Unable to update alert status. Please retry.', success: false });
    } finally {
      setTogglingId(null);
    }
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    if (!editModal) return;

    setIsSavingEdit(true);
    try {
      const payload = {
        id: Number(editModal.id),
        username: user,
        alert_name: editModal.alert_name,
        select_unit: editModal.select_unit,
        trigger_condition: editModal.trigger_condition,
        threshold_x: editModal.threshold_x,
        threshold_y: editModal.threshold_y,
        threshold_z: editModal.threshold_z,
        severity: editModal.severity,
        assigned_users: editModal.assigned_users.join(','),
        description: editModal.description,
        alert_active: editModal.alert_active ? 1 : 0,
      };

      const res = await fetch('/update_alert.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update alert');
      }
      setToast({ message: 'Alert updated successfully.', success: true });
      setEditModal(null);
      fetchAlerts();
    } catch (error) {
      console.error(error);
      setToast({ message: 'Could not save changes. Please try again.', success: false });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    try {
      const res = await fetch('/delete_alert.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(deleteCandidate.id), username: user }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete alert');
      }
      setToast({ message: 'Alert removed successfully.', success: true });
      setDeleteCandidate(null);
      fetchAlerts();
    } catch (error) {
      console.error(error);
      setToast({ message: 'Unable to remove alert. Please try again.', success: false });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#151C2A] via-[#0C1017] to-[#080B10]" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.45, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="absolute left-4 top-16 h-72 w-72 rounded-full bg-rose-500/25 blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
          className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-primary/30 blur-3xl"
        />
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed right-6 top-6 z-50 flex items-center gap-3 rounded-full border px-5 py-3 text-sm shadow-xl ${
              toast.success
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
                : 'border-rose-500/60 bg-rose-500/15 text-rose-200'
            }`}
          >
            <span className="text-lg">{toast.success ? '✅' : '⚠️'}</span>
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 py-16">
        <header className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-white/60">Alerts</p>
              <h1 className="text-4xl font-semibold">Active monitors</h1>
              <p className="max-w-xl text-sm text-white/65">
                Review, edit and manage alert automation. Tune thresholds, control delivery lists and pause alerting when maintenance is underway.
              </p>
            </div>
            <div className="flex flex-col items-end gap-3 text-xs text-white/60">
              <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 uppercase tracking-[0.3em]">
                {alerts.length} total
              </span>
              <span className="flex items-center gap-2 text-white/50">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400/70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-300" />
                </span>
                Real-time alerts
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <input
                type="search"
                placeholder="Search by name, unit or description"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40">⌕</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {severityFilters.map((filter) => {
                const active = severityFilter === filter;
                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setSeverityFilter(filter)}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] transition ${
                      active
                        ? 'border-transparent bg-gradient-to-r from-primary/80 to-rose-500/70 text-white shadow-lg shadow-primary/20'
                        : 'border-white/10 bg-white/5 text-white/60 hover:border-primary/40 hover:text-white'
                    }`}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        <section className="space-y-4">
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-[0px_20px_45px_-35px_rgba(244,63,94,0.45)] backdrop-blur">
            <div className="hidden grid-cols-[1.5fr_1.3fr_1fr_1.4fr_auto_auto] border-b border-white/5 px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/40 md:grid">
              <span>Alert</span>
              <span>Condition</span>
              <span>Severity</span>
              <span>Recipients</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>
            <div className="divide-y divide-white/5">
              {loading &&
                Array.from({ length: 5 }).map((_, idx) => (
                  <div
                    key={`skeleton-${idx}`}
                    className="grid gap-4 px-6 py-5 text-sm md:grid-cols-[1.5fr_1.3fr_1fr_1.4fr_auto_auto]"
                  >
                    <div className="h-4 w-3/4 animate-pulse rounded-full bg-white/10" />
                    <div className="h-4 w-2/3 animate-pulse rounded-full bg-white/10" />
                    <div className="h-4 w-24 animate-pulse rounded-full bg-white/10" />
                    <div className="h-4 w-4/5 animate-pulse rounded-full bg-white/10" />
                    <div className="h-9 w-20 animate-pulse rounded-full bg-white/10" />
                    <div className="flex items-center justify-end gap-2">
                      <span className="h-8 w-16 animate-pulse rounded-full bg-white/10" />
                      <span className="h-8 w-16 animate-pulse rounded-full bg-white/10" />
                    </div>
                  </div>
                ))}

              {!loading &&
                filteredAlerts.map((alert, idx) => {
                  const isActive = Number(alert.alert_active) === 1;
                  const recipients = alert.assigned_users
                    ? alert.assigned_users
                        .split(',')
                        .map((email) => email.trim())
                        .filter(Boolean)
                    : [];
                  const displayRecipients = recipients.slice(0, 3);
                  const remainingRecipients = recipients.length - displayRecipients.length;

                  return (
                    <motion.div
                      key={alert.id}
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.04, duration: 0.35, ease: 'easeOut' }}
                      className="grid gap-4 px-6 py-5 text-sm text-white/70 md:grid-cols-[1.5fr_1.3fr_1fr_1.4fr_auto_auto] md:items-center"
                    >
                      <div className="space-y-1">
                        <p className="text-base font-semibold text-white">{alert.alert_name}</p>
                        <p className="text-[0.65rem] uppercase tracking-[0.35em] text-white/45">
                          {alert.select_unit || 'No unit defined'}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs text-white/80">
                          <span>{alert.trigger_condition}</span>
                          <span className="text-white/40">·</span>
                          <span>
                            {['threshold_x', 'threshold_y', 'threshold_z']
                              .map((axis) => alert[axis] || '—')
                              .join(' / ')}
                          </span>
                          <span className="text-white/40">mm/s</span>
                        </div>
                        <p className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Thresholds</p>
                      </div>

                      <div className="space-y-1">
                        <span
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${
                            severityStyles[alert.severity] || 'border-white/10 bg-white/5 text-white/60'
                          }`}
                        >
                          <span className="text-base">{alert.severity === 'Good' ? '🟢' : alert.severity === 'Warning' ? '🟠' : '🔴'}</span>
                          {alert.severity || 'Unknown'}
                        </span>
                        <p className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Severity</p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {displayRecipients.length > 0 ? (
                            <>
                              {displayRecipients.map((email) => (
                                <span
                                  key={email}
                                  className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[0.65rem] text-white/70"
                                >
                                  {email}
                                </span>
                              ))}
                              {remainingRecipients > 0 && (
                                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[0.65rem] text-white/60">
                                  +{remainingRecipients} more
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-white/40">No recipients assigned</span>
                          )}
                        </div>
                        <p className="text-[0.65rem] uppercase tracking-[0.3em] text-white/35">Notification list</p>
                      </div>

                      <div className="md:justify-self-center">
                        <button
                          type="button"
                          onClick={() => toggleActive(alert)}
                          disabled={togglingId === alert.id}
                          className={`relative inline-flex h-10 w-24 items-center rounded-full border px-2 text-[0.65rem] font-semibold uppercase tracking-[0.35em] transition ${
                            isActive
                              ? 'border-emerald-400/70 bg-emerald-400/15 text-emerald-200'
                              : 'border-white/10 bg-white/5 text-white/60'
                          } ${togglingId === alert.id ? 'animate-pulse' : ''}`}
                        >
                          <span
                            className={`absolute left-1 top-1 h-8 w-8 rounded-full bg-white transition-all duration-300 ${
                              isActive
                                ? 'translate-x-[3.6rem] bg-emerald-400 shadow-lg shadow-emerald-500/40'
                                : ''
                            }`}
                          />
                          <span className="w-full text-center">{isActive ? 'Active' : 'Paused'}</span>
                        </button>
                      </div>

                      <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold uppercase tracking-[0.3em]">
                        <button
                          type="button"
                          onClick={() => setFocusAlert(alert)}
                          className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-white/70 transition hover:border-primary/60 hover:text-white"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(alert)}
                          className="rounded-full border border-primary/50 bg-primary/60 px-4 py-2 text-white transition hover:bg-primary/80"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(alert)}
                          className="rounded-full border border-rose-500/60 bg-rose-500/20 px-4 py-2 text-rose-100 transition hover:bg-rose-500/30"
                        >
                          Remove
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </div>

          {!loading && filteredAlerts.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-white/15 bg-white/5 p-12 text-center text-sm text-white/60">
              <span className="text-4xl">🚨</span>
              <p>No alerts match your filters right now.</p>
              <p className="max-w-md text-white/40">
                Adjust the search keywords or severity filter. New alerts appear here the moment they are created.
              </p>
            </div>
          )}
        </section>
      </div>

      <AnimatePresence>
        {focusAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur"
            onClick={() => setFocusAlert(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-xl rounded-3xl border border-white/10 bg-[#10141D] p-8 text-sm text-white/70 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setFocusAlert(null)}
                className="absolute right-5 top-5 text-xl text-white/40 transition hover:text-white"
                aria-label="Close"
              >
                ×
              </button>
              <h3 className="text-2xl font-semibold text-white">{focusAlert.alert_name}</h3>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">#{focusAlert.id}</p>
              <div className="mt-6 space-y-3">
                {Object.entries(focusAlert).map(([key, value]) => (
                  <div key={key} className="flex items-start justify-between gap-4">
                    <span className="text-white/50 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="max-w-xs text-right text-white">
                      {key === 'alert_active'
                        ? Number(value) === 1
                          ? 'Active'
                          : 'Inactive'
                        : value || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
          >
            <motion.form
              onSubmit={submitEdit}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-2xl space-y-6 rounded-3xl border border-white/10 bg-[#10141D] p-8 text-sm text-white"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-semibold">Edit alert</h3>
                  <p className="text-xs text-white/60">Adjust thresholds, recipients or severity.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="text-xl text-white/40 transition hover:text-white"
                >
                  ×
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Alert name</span>
                  <input
                    type="text"
                    value={editModal.alert_name}
                    onChange={(event) => updateEditField('alert_name', event.target.value)}
                    required
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Severity</span>
                  <select
                    value={editModal.severity}
                    onChange={(event) => updateEditField('severity', event.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="Good">Good</option>
                    <option value="Warning">Warning</option>
                    <option value="Failing">Failing</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Unit</span>
                  <select
                    value={editModal.select_unit || ''}
                    onChange={(event) => updateEditField('select_unit', event.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="">Choose unit</option>
                    {availableUnits.map((unit) => (
                      <option key={unit} value={unit} className="bg-[#0C1017] text-white">
                        {unit}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Trigger condition</span>
                  <select
                    value={editModal.trigger_condition}
                    onChange={(event) => updateEditField('trigger_condition', event.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="Above">Above threshold</option>
                    <option value="Below">Below threshold</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {['threshold_x', 'threshold_y', 'threshold_z'].map((axis) => (
                  <label key={axis} className="flex flex-col gap-2 text-xs">
                    <span className="text-white/60">
                      {axis.endsWith('x') ? 'Axis X' : axis.endsWith('y') ? 'Axis Y' : 'Axis Z'} (mm/s)
                    </span>
                    <input
                      type="number"
                      value={editModal[axis] || ''}
                      step="0.01"
                      min="0"
                      onChange={(event) => updateEditField(axis, event.target.value)}
                      className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                    />
                  </label>
                ))}
              </div>

              <label className="flex flex-col gap-2 text-xs">
                <span className="text-white/60">Description</span>
                <textarea
                  rows={3}
                  value={editModal.description || ''}
                  onChange={(event) => updateEditField('description', event.target.value)}
                  className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                />
              </label>

              <div className="grid gap-4 md:grid-cols-[auto_1fr] md:items-center">
                <label className="flex items-center gap-3 text-xs text-white/60">
                  <input
                    type="checkbox"
                    checked={editModal.alert_active}
                    onChange={(event) => updateEditField('alert_active', event.target.checked)}
                    className="h-5 w-5 rounded border border-white/20 bg-black/40 accent-emerald-400"
                  />
                  Alert active
                </label>
                <div className="space-y-3">
                  <p className="text-xs text-white/60">Recipients</p>
                  <div className="flex flex-wrap gap-2">
                    {editModal.assigned_users.map((email, idx) => (
                      <span
                        key={email}
                        className="flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[0.65rem] text-white/70"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEditRecipient(idx)}
                          className="rounded-full bg-white/10 px-2 py-0.5 text-white/60 transition hover:bg-rose-500/40 hover:text-white"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="email"
                    placeholder="Press enter to add"
                    value={emailDraft}
                    onChange={(event) => setEmailDraft(event.target.value)}
                    onKeyDown={addEditRecipient}
                    className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 text-xs font-semibold uppercase tracking-[0.3em]">
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-white/70 transition hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit}
                  className={`rounded-full border border-primary/50 bg-primary/60 px-6 py-2 text-white transition hover:bg-primary/80 ${
                    isSavingEdit ? 'cursor-not-allowed opacity-60' : ''
                  }`}
                >
                  {isSavingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteCandidate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-sm space-y-4 rounded-3xl border border-rose-500/40 bg-[#1A1F2A] p-6 text-sm text-white"
            >
              <h3 className="text-xl font-semibold text-white">Remove alert</h3>
              <p className="text-white/70">
                Are you sure you want to delete <span className="text-white">{deleteCandidate.alert_name}</span>? This cannot be undone.
              </p>
              <div className="flex justify-end gap-3 text-xs font-semibold uppercase tracking-[0.3em]">
                <button
                  type="button"
                  onClick={() => setDeleteCandidate(null)}
                  className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-white/70 transition hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="rounded-full border border-rose-500/60 bg-rose-500/30 px-4 py-2 text-rose-50 transition hover:bg-rose-500/50"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default CurrentAlerts;
